/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { ProcedureRouteGuard } from 'shared/routing/ProcedureRouteGuard';
import { NotFound } from 'shared/routing/NotFound';
import SwitchAccount from 'pages/userAccounts/SwitchAccount';
import OrganizationEditor from 'pages/organizationEditor/OrganizationEditor';
import UserProfile from 'pages/userProfile/UserProfile';
import UserManagementPage from 'pages/userTable/UserManagementPage';
import { EmailInvitationAccept } from 'pages/autoRegistration/EmailInvitationAccept';
import AccessibilityStatementPage from 'pages/accessibilityStatement/AccessibilityStatementPage';
import AppInitializer from './AppInitializer';
import Layout from './layout/Layout';

import DraftDetail from './pages/draftDetail/DraftDetail';
import SubmissionPreview from './pages/submissionPreview/SubmissionPreview';
import SendConfirmation from './pages/sendConfirmation/SendConfirmation';
import Contact from './pages/addressBook/Contact';
import EPCTIframe from './pages/ePCTSubmissions/EPCTIframe';
import EPIframe from './pages/epSubmissions/EPIframe';
import Dashboard from './pages/dashboard/Dashboard';
import DraftManagement from './pages/draftManagement/DraftManagement';
import { RoutePath } from './shared/enums/RoutePath';
import Alerts from './shared/alerts/Alerts';
import PaymentDetail from './payment/pages/PaymentDetail';
import { SigningForm } from './pages/signatureForm/SigningForm';
import { IsAuthorized } from './shared/authorization/IsAuthorized';
import { SubmissionHooks } from './shared/locking/SubmissionHooks';
import RequestReceipt from './pages/requestReceipt/RequestReceipt';
import { CommonAlertDialog } from './components/common/alertDialog/CommonAlertDialog';

export default function App() {
  return (
    <Alerts>
      <AppInitializer>
        <SubmissionHooks>
          <Layout>
            <CommonAlertDialog />
            <ProcedureRouteGuard>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Switch>
                  <Route exact path={[RoutePath.NOT_FOUND.path]}>
                    <NotFound />
                  </Route>

                  <Route exact path={RoutePath.ACCESSIBILITY_STATEMENT.path}>
                    <AccessibilityStatementPage />
                  </Route>

                  <Route exact path={RoutePath.USER_EMAIL_ACCEPT_INVITATION.path}>
                    <EmailInvitationAccept />
                  </Route>

                  <Route exact path={RoutePath.SWITCH_ACCOUNT.path}>
                    <SwitchAccount />
                  </Route>

                  <Route exact path={[RoutePath.USER_SEARCH.path]}>
                    <IsAuthorized>
                      <UserManagementPage />
                    </IsAuthorized>
                  </Route>

                  <Route
                    exact
                    path={[RoutePath.USER_PROFILE.path, RoutePath.USER_PROFILE_EDIT_ROLE.path, RoutePath.USER_PROFILE_VIEW.path]}
                  >
                    <IsAuthorized>
                      <UserProfile />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.ORGANIZATION_CREATE.path, RoutePath.ORGANIZATION_EDIT.path]}>
                    <IsAuthorized>
                      <OrganizationEditor />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.PROCEDURE_DRAFT.path, RoutePath.PROCEDURE_EDIT.path]}>
                    <IsAuthorized>
                      <DraftDetail />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.PROCEDURE_PAYMENT.path]}>
                    <IsAuthorized>
                      <PaymentDetail />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.PROCEDURE_VIEW.path]}>
                    <IsAuthorized>
                      <DraftDetail readOnly={true} />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.PROCEDURE_SEARCH.path, RoutePath.ADDRESSBOOK_SEARCH.path]}>
                    <IsAuthorized>
                      <DraftManagement />
                    </IsAuthorized>
                  </Route>

                  <Route
                    exact
                    path={[RoutePath.PROCEDURE_PREVIEW_SUBMISSION.path, RoutePath.PROCEDURE_NON_ORG_PREVIEW_SUBMISSION.path]}
                  >
                    <IsAuthorized>
                      <SubmissionPreview />
                    </IsAuthorized>
                  </Route>

                  <Route
                    exact
                    path={[RoutePath.PROCEDURE_SIGNING_SUBMISSION.path, RoutePath.PROCEDURE_NON_ORG_SIGNING_SUBMISSION.path]}
                  >
                    <IsAuthorized>
                      <SigningForm />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.PROCEDURE_REQUEST_RECEIPT_SUBMISSION.path]}>
                    <IsAuthorized>
                      <RequestReceipt />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.PROCEDURE_RECEIPTS_SUBMISSION.path]}>
                    <IsAuthorized>
                      <SendConfirmation />
                    </IsAuthorized>
                  </Route>

                  <Route
                    exact
                    path={[RoutePath.ADDRESSBOOK_CONTACT.path, RoutePath.ADDRESSBOOK_CONTACT_ID.path, RoutePath.ADDRESSBOOK_CONTACT_ID_EDIT.path]}
                  >
                    <IsAuthorized>
                      <Contact />
                    </IsAuthorized>
                  </Route>

                  <Route
                    exact
                    path={[RoutePath.EPCT_FILING.path, RoutePath.EPCT_SUMMARY_SUBMISSION.path, RoutePath.EPCT_EDIT_SUBMISSION.path]}
                  >
                    <IsAuthorized>
                      <EPCTIframe />
                    </IsAuthorized>
                  </Route>

                  <Route
                    exact
                    path={[RoutePath.EP_FILING.path, RoutePath.EP_SUMMARY_SUBMISSION.path, RoutePath.EP_EDIT_SUBMISSION.path]}
                  >
                    <IsAuthorized>
                      <EPIframe />
                    </IsAuthorized>
                  </Route>

                  <Route exact path={[RoutePath.DASHBOARD.path]}>
                    <IsAuthorized>
                      <Dashboard />
                    </IsAuthorized>
                  </Route>

                  <Route>
                    <NotFound />
                  </Route>
                </Switch>
              </MuiPickersUtilsProvider>
            </ProcedureRouteGuard>
          </Layout>
        </SubmissionHooks>
      </AppInitializer>
    </Alerts>
  );
}
